/**
 * The editor's half of the language: what can be written at a position, and
 * what the word under the cursor refers to.
 *
 * Everything here works on *half-typed* text, which is why it reads tokens
 * rather than an AST. A draft mid-keystroke usually doesn't parse — that's the
 * normal case, not the exceptional one — and completions that only work on
 * well-formed rules would be completions you can never use. So the scanner
 * takes the two things it needs from the token stream directly:
 *
 *   - which variables are bound, and to what: a parameter `b: battery` and a
 *     comprehension clause `for b in battery` are the only binders in the
 *     language, and both are three tokens.
 *   - what kind of position the cursor is in: after a `.` a column is expected,
 *     after a binder's `:` or `in` a relation, and otherwise a value.
 *
 * The results are plain data — no editor types — so this is testable on its own
 * and the Monaco adapter stays a translation layer.
 */

import { Catalog, RelationSchema, columnsOf, isValueCoercible } from './schema';
import { Span } from './diagnostics';
import { Token, tokenize, parseRuleWithSpans } from './parse';
import { BUILTIN_FUNCTIONS, Expr, Pred, Quantifier, Rule } from './ruleAst';
import {
  Type,
  TypeScope,
  boundIn,
  columnTypeOf,
  scopeOfRule,
  signatureOf,
  typeOfExpr,
  typeToString,
} from './types';

/** A cursor: 1-based line, and the column the next typed character would take. */
export interface Position {
  line: number;
  column: number;
}

export type CompletionKind = 'relation' | 'column' | 'variable' | 'keyword' | 'function';

export interface Completion {
  /** What to show in the list, and what gets typed unless `insertText` differs. */
  label: string;
  kind: CompletionKind;
  /** The one-line explanation beside the label — a relation's shape, a variable's range. */
  detail?: string;
  /**
   * What to insert, when that isn't the label — e.g. a relation completed in
   * value position brings its argument list with it. `$1`-style placeholders,
   * which the adapter hands to the editor as a snippet.
   */
  insertText?: string;
}

export interface Completions {
  items: Completion[];
  /** The word being replaced — empty (start == end) when the cursor isn't in one. */
  replace: Span;
}

/** A relation named at a position, and the extent of the name. */
export interface RelationReference {
  relation: string;
  span: Span;
}

/**
 * What the name under the cursor refers to: a relation, or one relation's
 * column.
 *
 * Both are things with a definition to go and look at — a relation's rows, and
 * the column of the table those rows have — which is what go-to-definition
 * needs. A column reference names the relation it belongs to, not the one its
 * type points at: `from` in `l.from` is defined by `line`, and that it *holds* a
 * bus is a separate fact, the one a hover tells you.
 */
export type Reference =
  | { kind: 'relation'; relation: string; span: Span }
  | { kind: 'column'; relation: string; column: string; span: Span };

/** The words the syntax reserves — offered alongside what's in scope. */
const KEYWORDS = ['def', 'for', 'in', 'if', 'elif', 'else', 'and', 'sum'];

const spanOf = (token: Token): Span => ({
  startLine: token.line,
  startColumn: token.column,
  endLine: token.endLine,
  endColumn: token.endColumn,
});

const emptySpanAt = (position: Position): Span => ({
  startLine: position.line,
  startColumn: position.column,
  endLine: position.line,
  endColumn: position.column,
});

const isKeyword = (token: Token | undefined, word: string): boolean =>
  !!token && token.kind === 'ident' && token.text === word;

const isPunct = (token: Token | undefined, text: string): boolean =>
  !!token && token.kind === 'punct' && token.text === text;

/** Tokens of a possibly-broken draft; leniently, so one open quote isn't fatal. */
const scan = (source: string): Token[] => tokenize(source, true).filter((t) => t.kind !== 'eof');

/**
 * The parameter list of the `def` head, as token indices of its parentheses.
 * A `:` inside it introduces a parameter's relation; the same `:` outside it
 * ends the head or a conditional's test.
 */
function paramRange(tokens: Token[]): { open: number; close: number } | undefined {
  if (!isKeyword(tokens[0], 'def') || !isPunct(tokens[2], '(')) return undefined;
  let depth = 0;
  for (let i = 2; i < tokens.length; i++) {
    if (isPunct(tokens[i], '(')) depth += 1;
    else if (isPunct(tokens[i], ')')) {
      depth -= 1;
      if (depth === 0) return { open: 2, close: i };
    }
  }
  return { open: 2, close: tokens.length };
}

/**
 * Every variable the text binds, and the relation it ranges over: the `def`
 * head's parameters and every `for … in …` clause.
 *
 * Flat, rather than scoped to the cursor: a sum's variable is only in scope
 * inside that sum, but a rule is a handful of lines and offering `l` while the
 * cursor is elsewhere costs a list entry, where getting the scoping wrong on
 * unparseable text costs the completion you wanted.
 */
export function bindersOf(source: string): Map<string, string> {
  const tokens = scan(source);
  const params = paramRange(tokens);
  const binders = new Map<string, string>();

  for (let i = 0; i < tokens.length; i++) {
    // `b: battery` — a parameter, which only the def head has.
    if (
      params &&
      i > params.open &&
      i + 2 <= params.close &&
      tokens[i].kind === 'ident' &&
      isPunct(tokens[i + 1], ':') &&
      tokens[i + 2]?.kind === 'ident'
    ) {
      binders.set(tokens[i].text, tokens[i + 2].text);
    }
    // `for b in battery` — a comprehension clause, anywhere.
    if (
      isKeyword(tokens[i], 'for') &&
      tokens[i + 1]?.kind === 'ident' &&
      isKeyword(tokens[i + 2], 'in') &&
      tokens[i + 3]?.kind === 'ident'
    ) {
      binders.set(tokens[i + 1].text, tokens[i + 3].text);
    }
  }
  return binders;
}

/** Index of the word the cursor sits in or at the end of, else -1. */
function wordIndexAt(tokens: Token[], position: Position): number {
  return tokens.findIndex(
    (t) =>
      t.kind === 'ident' &&
      t.line === position.line &&
      t.column <= position.column &&
      position.column <= t.endColumn,
  );
}

/** Index of the last token that ends at or before the cursor. */
function precedingIndex(tokens: Token[], position: Position): number {
  let found = -1;
  tokens.forEach((t, i) => {
    if (t.endLine < position.line || (t.endLine === position.line && t.endColumn <= position.column)) {
      found = i;
    }
  });
  return found;
}

const relationDetail = (schema: RelationSchema, catalog: Catalog): string =>
  `${schema.kind === 'decision' ? 'decision' : 'data'} · ${signatureOf(schema, catalog)}`;

/** A relation completed in value position brings its key arguments with it. */
const appliedInsert = (schema: RelationSchema): string =>
  `${schema.name}(${schema.keys.map((key, i) => `\${${i + 1}:${key}}`).join(', ')})`;

const relationItems = (catalog: Catalog): Completion[] =>
  Object.values(catalog).map((schema) => ({
    label: schema.name,
    kind: 'relation' as const,
    detail: relationDetail(schema, catalog),
  }));

/**
 * What can be written at `position`.
 *
 * Four positions, told apart by the token before the cursor:
 *   - after `.`, the columns of the relation the row variable ranges over;
 *   - after a binder's `:` or `in`, the relations that can be ranged over;
 *   - after `def`, nothing — the name is the user's to pick;
 *   - anywhere else a value belongs: the bound variables, the relations that
 *     coerce to a value (with their arguments), the builtins, the keywords.
 */
export function completionsAt(source: string, position: Position, catalog: Catalog): Completions {
  const tokens = scan(source);
  const wordIndex = wordIndexAt(tokens, position);
  const replace = wordIndex >= 0 ? spanOf(tokens[wordIndex]) : emptySpanAt(position);
  const prevIndex = wordIndex >= 0 ? wordIndex - 1 : precedingIndex(tokens, position);
  const prev = tokens[prevIndex];
  const beforePrev = tokens[prevIndex - 1];
  const params = paramRange(tokens);
  const binders = bindersOf(source);

  const none = { items: [], replace };

  // `b.` — the columns of whatever `b` ranges over.
  if (isPunct(prev, '.')) {
    if (!beforePrev || beforePrev.kind !== 'ident') return none;
    const schema = catalog[binders.get(beforePrev.text) ?? ''];
    if (!schema) return none;
    return {
      replace,
      items: columnsOf(schema).map((col) => ({
        label: col,
        kind: 'column' as const,
        detail: `${schema.name}.${col}: ${typeToString(columnTypeOf(schema, col, catalog))}`,
      })),
    };
  }

  // `b: ` in the parameter list, or `for b in ` — a relation to range over.
  const atParamRelation =
    isPunct(prev, ':') && params !== undefined && prevIndex > params.open && prevIndex < params.close;
  if (atParamRelation || isKeyword(prev, 'in')) {
    return { replace, items: relationItems(catalog) };
  }

  // The name in the `def` head, and a parameter's variable name, are new names.
  if (isKeyword(prev, 'def')) return none;
  if (params && (prevIndex === params.open || (isPunct(prev, ',') && prevIndex < params.close))) return none;

  const items: Completion[] = [];
  for (const [variable, relation] of binders) {
    items.push({
      label: variable,
      kind: 'variable',
      detail: catalog[relation] ? `${variable}: ${relation}` : `${variable}: ${relation} (unknown relation)`,
    });
  }
  for (const schema of Object.values(catalog)) {
    // Only a value-coercible relation *is* a value; an entity table is ranged
    // over and read with `.attr`, so it has no place in an expression.
    if (!isValueCoercible(schema)) continue;
    items.push({
      label: schema.name,
      kind: 'relation',
      detail: relationDetail(schema, catalog),
      insertText: appliedInsert(schema),
    });
  }
  for (const fn of BUILTIN_FUNCTIONS) {
    items.push({ label: fn, kind: 'function', detail: 'builtin function', insertText: `${fn}($1)` });
  }
  items.push({
    label: 'sum',
    kind: 'function',
    detail: 'sum(<expr> for <var> in <relation>)',
    insertText: 'sum($1 for ${2:x} in ${3:relation})',
  });
  for (const word of KEYWORDS) {
    if (word !== 'sum') items.push({ label: word, kind: 'keyword' });
  }
  return { items, replace };
}

/**
 * What the word at `position` refers to, if it refers to anything.
 *
 * A **relation**, when the word names one and is used as one: applied
 * (`demand(n, t)`), or ranged over (`b: battery`, `for l in line`). Not a
 * variable, not a builtin, and not the rule's own name in its `def` head, even
 * where a relation happens to share that word.
 *
 * A **column**, when the word follows a `.` on a bound row variable and the
 * relation that variable ranges over really has it: `from` in `l.from`.
 */
export function referenceAt(source: string, position: Position, catalog: Catalog): Reference | undefined {
  const tokens = scan(source);
  const index = tokens.findIndex(
    (t) =>
      t.kind === 'ident' &&
      t.line === position.line &&
      t.column <= position.column &&
      position.column < t.endColumn,
  );
  if (index < 0) return undefined;

  const token = tokens[index];
  const prev = tokens[index - 1];
  const next = tokens[index + 1];
  const params = paramRange(tokens);

  // `l.from` — a column of whatever `l` ranges over.
  if (isPunct(prev, '.') && tokens[index - 2]?.kind === 'ident') {
    const schema = catalog[bindersOf(source).get(tokens[index - 2].text) ?? ''];
    if (!schema || !columnsOf(schema).includes(token.text)) return undefined;
    return { kind: 'column', relation: schema.name, column: token.text, span: spanOf(token) };
  }

  if (!catalog[token.text]) return undefined;
  // `def demand(…)` names a rule, even when a relation shares the word.
  if (isKeyword(prev, 'def')) return undefined;

  const applied = isPunct(next, '(') && !BUILTIN_FUNCTIONS.has(token.text);
  const rangedOver =
    isKeyword(prev, 'in') ||
    (isPunct(prev, ':') && params !== undefined && index - 1 > params.open && index - 1 < params.close);

  return applied || rangedOver ? { kind: 'relation', relation: token.text, span: spanOf(token) } : undefined;
}

/** {@link referenceAt} narrowed to relations — what a relation's own hover wants. */
export function relationReferenceAt(
  source: string,
  position: Position,
  catalog: Catalog,
): RelationReference | undefined {
  const reference = referenceAt(source, position, catalog);
  return reference?.kind === 'relation' ? { relation: reference.relation, span: reference.span } : undefined;
}

// ---------------------------------------------------------------------------
// The type at a position
// ---------------------------------------------------------------------------

/** What the text under the cursor is worth, and which text that is. */
export interface TypedExpression {
  /** The source of the node the cursor is in. */
  text: string;
  span: Span;
  type: Type;
}

/** Whether a position falls inside a span (its end column is exclusive). */
function contains(span: Span, position: Position): boolean {
  const afterStart =
    position.line > span.startLine ||
    (position.line === span.startLine && position.column >= span.startColumn);
  const beforeEnd =
    position.line < span.endLine || (position.line === span.endLine && position.column < span.endColumn);
  return afterStart && beforeEnd;
}

/** Every expression a predicate compares — its operands, through any `and`. */
const predExprs = (p: Pred): Expr[] =>
  p.kind === 'and' ? [...predExprs(p.left), ...predExprs(p.right)] : [p.left, p.right];

/**
 * The expressions directly inside one — including the operands of the
 * predicates it carries, since a filter's `l.to == n` is as hoverable as the
 * summand beside it.
 */
function childExprs(e: Expr): Expr[] {
  switch (e.kind) {
    case 'apply':
      return e.args.map((arg) => arg.value);
    case 'call':
      return e.args;
    case 'neg':
      return [e.operand];
    case 'binary':
      return [e.left, e.right];
    case 'sum':
      return [e.body, ...e.over.flatMap((q) => (q.where ? predExprs(q.where) : []))];
    case 'cond':
      return [...predExprs(e.when), e.then, e.otherwise];
    default:
      return [];
  }
}

/** The expressions of a rule's body, whichever shape it has. */
const bodyExprs = (rule: Rule): Expr[] => {
  const body = rule.body;
  if (body.kind === 'define') return [body.expr];
  if (body.kind === 'range') return [body.lower, body.expr, body.upper];
  return [body.left, body.right];
};

interface Found {
  node: Expr;
  scope: TypeScope;
}

/**
 * The innermost expression covering `position`, with the scope in force there.
 *
 * Innermost, so hovering `l.from` inside `theta(l.from, t) / l.reactance` asks
 * about the attribute rather than the division — and hovering the `/` between
 * them, which no child covers, asks about the division.
 */
function findExpr(
  e: Expr,
  scope: TypeScope,
  spans: ReadonlyMap<unknown, Span>,
  position: Position,
  catalog: Catalog,
): Found | undefined {
  const span = spans.get(e);
  if (span && !contains(span, position)) return undefined;
  // A sum's clauses are in scope for its body and for the filters beside it.
  const inner = e.kind === 'sum' ? boundIn(e.over, scope, catalog) : scope;
  for (const child of childExprs(e)) {
    const found = findExpr(child, inner, spans, position, catalog);
    if (found) return found;
  }
  return span ? { node: e, scope } : undefined;
}

/**
 * The type of whatever the cursor is in.
 *
 * Two ways in, because a rule being edited is often a rule that doesn't parse.
 * When it parses, this is the real thing: the innermost node covering the
 * position, typed in the scope that reaches it. When it doesn't, a bound
 * variable or an attribute read is still recognisable from the tokens alone —
 * which covers the parameter list, where there's no expression to find in the
 * first place.
 */
export function typeAt(source: string, position: Position, catalog: Catalog): TypedExpression | undefined {
  const lines = source.split('\n');
  const textOf = (span: Span): string =>
    span.startLine === span.endLine
      ? lines[span.startLine - 1].slice(span.startColumn - 1, span.endColumn - 1)
      : lines
          .slice(span.startLine - 1, span.endLine)
          .join('\n')
          .slice(span.startColumn - 1);

  try {
    const { rule, spans } = parseRuleWithSpans(source);
    const scope = scopeOfRule(rule, catalog);
    const roots: Expr[] = [
      ...rule.quantifiers.flatMap((q: Quantifier) => (q.where ? predExprs(q.where) : [])),
      ...bodyExprs(rule),
    ];
    for (const root of roots) {
      const found = findExpr(root, scope, spans, position, catalog);
      if (found) {
        const span = spans.get(found.node)!;
        return { text: textOf(span), span, type: typeOfExpr(found.node, found.scope, catalog) };
      }
    }
  } catch {
    // Not parseable yet — fall through to the token reading.
  }
  return typeAtToken(source, position, catalog);
}

/** The types readable straight off the tokens: a bound variable, an attribute read. */
function typeAtToken(source: string, position: Position, catalog: Catalog): TypedExpression | undefined {
  const tokens = scan(source);
  const index = tokens.findIndex(
    (t) =>
      t.kind === 'ident' &&
      t.line === position.line &&
      t.column <= position.column &&
      position.column < t.endColumn,
  );
  if (index < 0) return undefined;

  const token = tokens[index];
  const binders = bindersOf(source);
  const scope: TypeScope = boundIn(
    [...binders].map(([variable, relation]) => ({ var: variable, relation })),
    new Map(),
    catalog,
  );

  // `b.efficiency` — the column, reported with the dotted text it was read from.
  if (isPunct(tokens[index - 1], '.') && tokens[index - 2]?.kind === 'ident') {
    const owner = tokens[index - 2];
    const schema = catalog[binders.get(owner.text) ?? ''];
    if (!schema) return undefined;
    return {
      text: `${owner.text}.${token.text}`,
      span: { ...spanOf(token), startLine: owner.line, startColumn: owner.column },
      type: columnTypeOf(schema, token.text, catalog),
    };
  }

  const type = scope.get(token.text);
  return type ? { text: token.text, span: spanOf(token), type } : undefined;
}
